import { Typography, Tabs, Row, Col, Button } from "antd"
import { useState } from "react"
import { observer } from "mobx-react"
import { useTranslation } from "react-i18next"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { MarketplacesViewModel } from "./MarketplacesViewModel"
import MarketplaceGiftCardFeeCard from "./components/MarketplaceGiftCardFeeCard"
import { MarketplaceCode } from "../../../domain/entities/Marketplace"
import { Card as CustomCard } from "../../styledComponents/CustomCard/Card"
import { Card } from "antd"
import MultiSwitch from "../../components/Switch/MultiSwitch"
import BannersFormView from "../../components/BannersFormView/BannersFormView"
import MainForm from "../../components/Form/MainForm"
import MarketplaceLandingReviewsFormSection from "./components/MarketplaceLandingReviewsFormSection"
import MarketplaceLandingReviewModal from "./components/MarketplaceLandingReviewModal"
import MarketplaceTipsFormSection from "./components/MarketplaceTipsFormSection"
import MarketplaceTipModal from "./components/MarketplaceTipModal"

const { Title } = Typography

const MarketplacesView = () => {
	const { t } = useTranslation("marketplaces")
	const { t: tBanners } = useTranslation("banners")
	const { t: tMainForm } = useTranslation("mainForm")
	const viewModel = useContainerInjection<MarketplacesViewModel>("MarketplacesViewModel")
	const [giftcardCollapsed, setGiftcardCollapsed] = useState<Record<string, boolean>>({
		[MarketplaceCode.SPAIN]: false,
		[MarketplaceCode.MEXICO]: false
	})
	const [reviewsCollapsed, setReviewsCollapsed] = useState<Record<string, boolean>>({
		[MarketplaceCode.SPAIN]: true,
		[MarketplaceCode.MEXICO]: true
	})
	const [reviewModalVisible, setReviewModalVisible] = useState<Record<string, boolean>>({
		[MarketplaceCode.SPAIN]: false,
		[MarketplaceCode.MEXICO]: false
	})
	const [tipsCollapsed, setTipsCollapsed] = useState<Record<string, boolean>>({
		[MarketplaceCode.SPAIN]: true,
		[MarketplaceCode.MEXICO]: true
	})
	const [tipModalVisible, setTipModalVisible] = useState<Record<string, boolean>>({
		[MarketplaceCode.SPAIN]: false,
		[MarketplaceCode.MEXICO]: false
	})

	const spainMarketplace = viewModel.marketplaces.find(m => m.code === MarketplaceCode.SPAIN)
	const mexicoMarketplace = viewModel.marketplaces.find(m => m.code === MarketplaceCode.MEXICO)
	const availableSpainReviews = viewModel.landingReviewsCatalog.filter(
		review => !viewModel.landingReviewsEs.some(selectedReview => selectedReview.id === review.id)
	)
	const availableMexicoReviews = viewModel.landingReviewsCatalog.filter(
		review => !viewModel.landingReviewsMx.some(selectedReview => selectedReview.id === review.id)
	)
	const availableSpainTips = viewModel.tipsCatalog.filter(
		tip => !viewModel.marketplaceTipsEs.some(selectedTip => selectedTip.tipId === tip.id)
	)
	const availableMexicoTips = viewModel.tipsCatalog.filter(
		tip => !viewModel.marketplaceTipsMx.some(selectedTip => selectedTip.tipId === tip.id)
	)

	const submitForm = async () => {
		if (viewModel.hasIncompleteBanners()) {
			throw { message: tBanners("validations.incompleteBanner") }
		}
		await viewModel.saveBanners()
	}

	const tabItems = [
		{
			key: MarketplaceCode.SPAIN,
			label: t("tabs.spain"),
			children: (
				<div>
					<Card
						bordered={false}
						className="criclebox"
						title={
							<Row justify="space-between" align="middle">
								<Title style={{ marginTop: 8 }} level={5}>
									{t("giftCardFeeConfig")}
								</Title>
								<div
									style={{
										border: "1px solid #8f8f8f",
										paddingLeft: 4,
										paddingRight: 4,
										color: "#8f8f8f",
										cursor: "pointer"
									}}
									onClick={() =>
										setGiftcardCollapsed(prev => ({
											...prev,
											[MarketplaceCode.SPAIN]: !prev[MarketplaceCode.SPAIN]
										}))
									}
								>
									{giftcardCollapsed[MarketplaceCode.SPAIN] ? "+" : "-"}
								</div>
							</Row>
						}
						style={{ marginBottom: 24 }}
					>
						{!giftcardCollapsed[MarketplaceCode.SPAIN] && (
							<MarketplaceGiftCardFeeCard
								marketplace={spainMarketplace}
								isLoading={viewModel.isLoading}
								onSave={(code, fee) => viewModel.saveMarketplace(code, fee)}
							/>
						)}
					</Card>
					<BannersFormView
						banners={viewModel.bannersEs}
						title={t("banners")}
						language={viewModel.selectedLanguage}
						microsite="ES"
						onBannerChange={banners => viewModel.setBanners(banners, "ES")}
						isLoading={viewModel.isLoading}
						defaultCollapsed={true}
						submitButtonText={tMainForm("save")}
					/>
					<Card
						bordered={false}
						className="criclebox"
						style={{ marginTop: 24 }}
						title={
							<Row justify="space-between" align="middle">
								<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
									<Title style={{ marginTop: 8, marginBottom: 0 }} level={5}>
										{t("reviews")}
									</Title>
									<Button
										type="primary"
										onClick={() =>
											setReviewModalVisible(prev => ({
												...prev,
												[MarketplaceCode.SPAIN]: true
											}))
										}
									>
										{t("landingReviewsInfo.add")}
									</Button>
								</div>
								<div
									style={{
										border: "1px solid #8f8f8f",
										paddingLeft: 4,
										paddingRight: 4,
										color: "#8f8f8f",
										cursor: "pointer"
									}}
									onClick={() =>
										setReviewsCollapsed(prev => ({
											...prev,
											[MarketplaceCode.SPAIN]: !prev[MarketplaceCode.SPAIN]
										}))
									}
								>
									{reviewsCollapsed[MarketplaceCode.SPAIN] ? "+" : "-"}
								</div>
							</Row>
						}
					>
						{!reviewsCollapsed[MarketplaceCode.SPAIN] && (
							<>
								<MarketplaceLandingReviewsFormSection
									reviews={viewModel.landingReviewsEs}
									isLoading={viewModel.isLoading}
									onDelete={id => viewModel.removeLandingReviewBySlug("es", id)}
									onChange={(id, review) =>
										viewModel.updateLandingReviewWeightBySlug("es", id, Number(review.weight))
									}
								/>
								<div
									style={{ display: "flex", justifyContent: "center", width: "100%", marginTop: 24 }}
								>
									<Button
										type="primary"
										onClick={() => viewModel.saveLandingReviews(MarketplaceCode.SPAIN)}
										disabled={viewModel.isLoading}
									>
										{tMainForm("save")}
									</Button>
								</div>
							</>
						)}
					</Card>
					<MarketplaceLandingReviewModal
						visible={reviewModalVisible[MarketplaceCode.SPAIN]}
						reviews={availableSpainReviews}
						onCancel={() =>
							setReviewModalVisible(prev => ({
								...prev,
								[MarketplaceCode.SPAIN]: false
							}))
						}
						onConfirm={review => {
							viewModel.addLandingReviewBySlug("es", review)
							setReviewModalVisible(prev => ({
								...prev,
								[MarketplaceCode.SPAIN]: false
							}))
						}}
					/>
					<Card
						bordered={false}
						className="criclebox"
						style={{ marginTop: 24 }}
						title={
							<Row justify="space-between" align="middle">
								<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
									<Title style={{ marginTop: 8, marginBottom: 0 }} level={5}>
										{t("tipsInfo.title")}
									</Title>
									<Button
										type="primary"
										onClick={() =>
											setTipModalVisible(prev => ({
												...prev,
												[MarketplaceCode.SPAIN]: true
											}))
										}
									>
										{t("tipsInfo.add")}
									</Button>
								</div>
								<div
									style={{
										border: "1px solid #8f8f8f",
										paddingLeft: 4,
										paddingRight: 4,
										color: "#8f8f8f",
										cursor: "pointer"
									}}
									onClick={() =>
										setTipsCollapsed(prev => ({
											...prev,
											[MarketplaceCode.SPAIN]: !prev[MarketplaceCode.SPAIN]
										}))
									}
								>
									{tipsCollapsed[MarketplaceCode.SPAIN] ? "+" : "-"}
								</div>
							</Row>
						}
					>
						{!tipsCollapsed[MarketplaceCode.SPAIN] && (
							<>
								<MarketplaceTipsFormSection
									tips={viewModel.marketplaceTipsEs}
									isLoading={viewModel.isLoading}
									onDelete={tipId =>
										viewModel.removeMarketplaceTipByCode(MarketplaceCode.SPAIN, tipId)
									}
									onChange={(tipId, item) =>
										viewModel.updateMarketplaceTipPositionByCode(
											MarketplaceCode.SPAIN,
											tipId,
											Number(item.position)
										)
									}
								/>
								<div
									style={{ display: "flex", justifyContent: "center", width: "100%", marginTop: 24 }}
								>
									<Button
										type="primary"
										onClick={() => viewModel.saveMarketplaceTips(MarketplaceCode.SPAIN)}
										disabled={viewModel.isLoading}
									>
										{tMainForm("save")}
									</Button>
								</div>
							</>
						)}
					</Card>
					<MarketplaceTipModal
						visible={tipModalVisible[MarketplaceCode.SPAIN]}
						tips={availableSpainTips}
						onCancel={() =>
							setTipModalVisible(prev => ({
								...prev,
								[MarketplaceCode.SPAIN]: false
							}))
						}
						onConfirm={tip => {
							viewModel.addMarketplaceTipByCode(MarketplaceCode.SPAIN, tip)
							setTipModalVisible(prev => ({
								...prev,
								[MarketplaceCode.SPAIN]: false
							}))
						}}
					/>
				</div>
			)
		},
		{
			key: MarketplaceCode.MEXICO,
			label: t("tabs.mexico"),
			children: (
				<div>
					<Card
						bordered={false}
						className="criclebox"
						title={
							<Row justify="space-between" align="middle">
								<Title style={{ marginTop: 8 }} level={5}>
									{t("giftCardFeeConfig")}
								</Title>
								<div
									style={{
										border: "1px solid #8f8f8f",
										paddingLeft: 4,
										paddingRight: 4,
										color: "#8f8f8f",
										cursor: "pointer"
									}}
									onClick={() =>
										setGiftcardCollapsed(prev => ({
											...prev,
											[MarketplaceCode.MEXICO]: !prev[MarketplaceCode.MEXICO]
										}))
									}
								>
									{giftcardCollapsed[MarketplaceCode.MEXICO] ? "+" : "-"}
								</div>
							</Row>
						}
						style={{ marginBottom: 24 }}
					>
						{!giftcardCollapsed[MarketplaceCode.MEXICO] && (
							<MarketplaceGiftCardFeeCard
								marketplace={mexicoMarketplace}
								isLoading={viewModel.isLoading}
								onSave={(code, fee) => viewModel.saveMarketplace(code, fee)}
							/>
						)}
					</Card>
					<BannersFormView
						banners={viewModel.bannersMx}
						title={t("banners")}
						language={viewModel.selectedLanguage}
						microsite="MX"
						onBannerChange={banners => viewModel.setBanners(banners, "MX")}
						isLoading={viewModel.isLoading}
						defaultCollapsed={true}
						submitButtonText={tMainForm("save")}
					/>
					<Card
						bordered={false}
						className="criclebox"
						style={{ marginTop: 24 }}
						title={
							<Row justify="space-between" align="middle">
								<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
									<Title style={{ marginTop: 8, marginBottom: 0 }} level={5}>
										{t("reviews")}
									</Title>
									<Button
										type="primary"
										onClick={() =>
											setReviewModalVisible(prev => ({
												...prev,
												[MarketplaceCode.MEXICO]: true
											}))
										}
									>
										{t("landingReviewsInfo.add")}
									</Button>
								</div>
								<div
									style={{
										border: "1px solid #8f8f8f",
										paddingLeft: 4,
										paddingRight: 4,
										color: "#8f8f8f",
										cursor: "pointer"
									}}
									onClick={() =>
										setReviewsCollapsed(prev => ({
											...prev,
											[MarketplaceCode.MEXICO]: !prev[MarketplaceCode.MEXICO]
										}))
									}
								>
									{reviewsCollapsed[MarketplaceCode.MEXICO] ? "+" : "-"}
								</div>
							</Row>
						}
					>
						{!reviewsCollapsed[MarketplaceCode.MEXICO] && (
							<>
								<MarketplaceLandingReviewsFormSection
									reviews={viewModel.landingReviewsMx}
									isLoading={viewModel.isLoading}
									onDelete={id => viewModel.removeLandingReviewBySlug("mx", id)}
									onChange={(id, review) =>
										viewModel.updateLandingReviewWeightBySlug("mx", id, Number(review.weight))
									}
								/>
								<div
									style={{ display: "flex", justifyContent: "center", width: "100%", marginTop: 24 }}
								>
									<Button
										type="primary"
										onClick={() => viewModel.saveLandingReviews(MarketplaceCode.MEXICO)}
										disabled={viewModel.isLoading}
									>
										{tMainForm("save")}
									</Button>
								</div>
							</>
						)}
					</Card>
					<MarketplaceLandingReviewModal
						visible={reviewModalVisible[MarketplaceCode.MEXICO]}
						reviews={availableMexicoReviews}
						onCancel={() =>
							setReviewModalVisible(prev => ({
								...prev,
								[MarketplaceCode.MEXICO]: false
							}))
						}
						onConfirm={review => {
							viewModel.addLandingReviewBySlug("mx", review)
							setReviewModalVisible(prev => ({
								...prev,
								[MarketplaceCode.MEXICO]: false
							}))
						}}
					/>
					<Card
						bordered={false}
						className="criclebox"
						style={{ marginTop: 24 }}
						title={
							<Row justify="space-between" align="middle">
								<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
									<Title style={{ marginTop: 8, marginBottom: 0 }} level={5}>
										{t("tipsInfo.title")}
									</Title>
									<Button
										type="primary"
										onClick={() =>
											setTipModalVisible(prev => ({
												...prev,
												[MarketplaceCode.MEXICO]: true
											}))
										}
									>
										{t("tipsInfo.add")}
									</Button>
								</div>
								<div
									style={{
										border: "1px solid #8f8f8f",
										paddingLeft: 4,
										paddingRight: 4,
										color: "#8f8f8f",
										cursor: "pointer"
									}}
									onClick={() =>
										setTipsCollapsed(prev => ({
											...prev,
											[MarketplaceCode.MEXICO]: !prev[MarketplaceCode.MEXICO]
										}))
									}
								>
									{tipsCollapsed[MarketplaceCode.MEXICO] ? "+" : "-"}
								</div>
							</Row>
						}
					>
						{!tipsCollapsed[MarketplaceCode.MEXICO] && (
							<>
								<MarketplaceTipsFormSection
									tips={viewModel.marketplaceTipsMx}
									isLoading={viewModel.isLoading}
									onDelete={tipId =>
										viewModel.removeMarketplaceTipByCode(MarketplaceCode.MEXICO, tipId)
									}
									onChange={(tipId, item) =>
										viewModel.updateMarketplaceTipPositionByCode(
											MarketplaceCode.MEXICO,
											tipId,
											Number(item.position)
										)
									}
								/>
								<div
									style={{ display: "flex", justifyContent: "center", width: "100%", marginTop: 24 }}
								>
									<Button
										type="primary"
										onClick={() => viewModel.saveMarketplaceTips(MarketplaceCode.MEXICO)}
										disabled={viewModel.isLoading}
									>
										{tMainForm("save")}
									</Button>
								</div>
							</>
						)}
					</Card>
					<MarketplaceTipModal
						visible={tipModalVisible[MarketplaceCode.MEXICO]}
						tips={availableMexicoTips}
						onCancel={() =>
							setTipModalVisible(prev => ({
								...prev,
								[MarketplaceCode.MEXICO]: false
							}))
						}
						onConfirm={tip => {
							viewModel.addMarketplaceTipByCode(MarketplaceCode.MEXICO, tip)
							setTipModalVisible(prev => ({
								...prev,
								[MarketplaceCode.MEXICO]: false
							}))
						}}
					/>
				</div>
			)
		}
	]

	return (
		<MainForm
			hideFormButtons={true}
			hideFormCancelButton={true}
			disabled={viewModel.isLoading}
			onSubmitForm={() => submitForm()}
			isUpdating={true}
		>
			<div className="tabled">
				<Row gutter={[24, 0]}>
					<Col xs={24} xl={24}>
						<Card bordered={true} className="criclebox tablespace">
							<div style={{ display: "flex", flexDirection: "column", padding: 20 }}>
								<Title style={{ marginTop: 0 }} level={4}>
									{tBanners("selectLanguage")}
								</Title>
								<MultiSwitch
									options={[
										{ label: "ES", value: "ES" },
										{ label: "EN", value: "EN" },
										{ label: "CA", value: "CA" }
									]}
									value={viewModel.selectedLanguage}
									onChange={lang => viewModel.setSelectedLanguage(lang)}
									buttonsStyles={{ width: 80 }}
								/>
							</div>
						</Card>
					</Col>
				</Row>
			</div>

			<div className="tabled" style={{ marginTop: 30 }}>
				<Row gutter={[24, 0]}>
					<Col xs={24} xl={24}>
						<CustomCard
							bordered={false}
							className="criclebox tablespace mb-24"
							title={
								<div style={{ display: "flex", alignItems: "center" }}>
									<Title style={{ marginTop: 8 }} level={4}>
										{t("title")}
									</Title>
								</div>
							}
							bodyStyle={{ padding: "24px" }}
						>
							<Tabs items={tabItems} defaultActiveKey={MarketplaceCode.SPAIN} />
						</CustomCard>
					</Col>
				</Row>
			</div>
		</MainForm>
	)
}

export default observer(MarketplacesView)
